import { ButtonProps, ConfirmationDialog, DialogProps } from '@elipssolution/harfang';
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

type ButtonDetails = {
	label: string;
	color: ButtonProps['color'];
	icon: ButtonProps['startIcon'];
};

type ConfirmationDialogProps = {
	title?: string;
	content?: string;
	confirmButtonDetails?: Partial<ButtonDetails>;
	cancelButtonDetails?: Partial<Pick<ButtonDetails, 'label'>>;
};

type CloseDialogConfirmationDialogProps = Pick<ConfirmationDialogProps, 'title' | 'content'>;

type ConfirmationDialogContextType = {
	confirmAction: (props?: ConfirmationDialogProps) => Promise<boolean>;
	confirmCloseDialog: (props?: CloseDialogConfirmationDialogProps) => Promise<boolean>;
};

const ConfirmationDialogContext = createContext<ConfirmationDialogContextType | undefined>(undefined);

type ConfirmationDialogProviderProps = {
	children: ReactNode;
};

const defaultDialogProps: ConfirmationDialogProps = {
	title: 'Êtes-vous sûr de vouloir effectuer cette action ?',
	cancelButtonDetails: {
		label: 'Annuler',
	},
	confirmButtonDetails: {
		label: 'Confirmer',
	},
};

const ConfirmationDialogProvider = ({ children }: ConfirmationDialogProviderProps) => {
	const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();
	const [confirmationResolve, setConfirmationResolve] = useState<(value: boolean) => void>();

	const confirmAction = (props?: ConfirmationDialogProps) => {
		setDialogProps({
			...defaultDialogProps,
			...props,
		});

		return new Promise<boolean>((resolve) => {
			setConfirmationResolve(() => resolve);
		});
	};

	const confirmCloseDialog = (props?: CloseDialogConfirmationDialogProps) => {
		setDialogProps({
			...defaultDialogProps,
			...props,
		});

		return new Promise<boolean>((resolve) => {
			setConfirmationResolve(() => resolve);
		});
	};

	const handleClose = () => {
		setDialogProps(undefined);
		confirmationResolve?.(false);
	};

	const handleConfirm = () => {
		setDialogProps(undefined);
		confirmationResolve?.(true);
	};

	const actionsDialog: DialogProps['actionsDialog'] = [
		{ id: 'close', ...dialogProps?.cancelButtonDetails, onClick: handleClose },
		{ id: 'confirm', ...dialogProps?.confirmButtonDetails, onClick: handleConfirm, variant: 'contained' },
	];

	const contextValue = useMemo(
		() => ({
			confirmAction,
			confirmCloseDialog,
		}),
		[],
	);

	const renderConfirmationDialog = () => {
		const { title, content } = dialogProps ?? {};

		if (!title) {
			return undefined;
		}

		return (
			<ConfirmationDialog
				open={!!dialogProps}
				onClose={handleClose}
				actionsDialog={actionsDialog}
				title={title}
				content={content}
			/>
		);
	};

	return (
		<ConfirmationDialogContext.Provider value={contextValue}>
			{children}
			{dialogProps && renderConfirmationDialog()}
		</ConfirmationDialogContext.Provider>
	);
};

export const useConfirmationDialogContext = () => {
	const context = useContext(ConfirmationDialogContext);

	if (context === undefined) {
		throw new Error('ConfirmationDialogContext should be used within the ConfirmationDialogProvider');
	}

	return context;
};

export default ConfirmationDialogProvider;
